(function () {
  'use strict';


  /* =========================================================
     VEDA V3
     PUBLIC / LOCAL KNOWLEDGE ENGINE
     ========================================================= */


  const STOP_WORDS = new Set([
    'a',
    'an',
    'and',
    'are',
    'as',
    'at',
    'be',
    'can',
    'do',
    'does',
    'for',
    'from',
    'how',
    'i',
    'in',
    'is',
    'it',
    'me',
    'my',
    'of',
    'on',
    'or',
    'our',
    'please',
    'tell',
    'that',
    'the',
    'this',
    'to',
    'us',
    'what',
    'when',
    'where',
    'which',
    'who',
    'why',
    'with',
    'you',
    'your',

    /*
     * Brand words are deliberately ignored
     * when scoring generic FAQs.
     */
    'vedica',
    'artistry'
  ]);


  const GREETINGS =
    /^(hi+|hii+|hiii+|hello+|hey+|heyy+|hlo+|hola|yo|namaste|good morning|good afternoon|good evening)[!. ]*$/i;


  const THANKS =
    /^(thank you|thanks|thankyou|thx|ty|thanks a lot|okay thanks|ok thanks)[!. ]*$/i;


  const BYE =
    /^(bye|goodbye|see you|cya|good night|goodnight)[!. ]*$/i;


  const AGENT_IDENTITY =
    /^(who are you|what are you|what is your name|your name|are you ai|are you human)[?.! ]*$/i;



  /* =========================================================
     PRIVATE REQUESTS — BLOCKED FOR NOW
     ========================================================= */

  const PRIVATE_PATTERNS = [

    /\bmy\s+(order|orders|parcel|shipment|tracking|awb|refund|payment|delivery|account)\b/i,

    /\bwhere\s+(is|are)\s+my\b/i,

    /\btrack\s+(my|order|parcel|shipment)\b/i,

    /\bshow\s+my\s+orders?\b/i,

    /\blist\s+my\s+orders?\b/i,

    /\bmy\s+(refund|payment|tracking|order)\s+status\b/i,

    /\bdid\s+you\s+(receive|get)\s+my\s+payment\b/i,

    /\bhas\s+my\s+(refund|order|parcel|shipment)\b/i,

    /\bwhen\s+will\s+my\s+(refund|order|parcel|shipment)\b/i,

    /\bmy\s+awb\b/i,

    /#[A-Z0-9_-]{3,}/i,

    /\border\s*(id|number|no\.?)\s*[:#-]?\s*[A-Z0-9_-]{3,}/i

  ];



  /* =========================================================
     TEXT HELPERS
     ========================================================= */

  function normalize(value) {

    return String(value || '')

      .toLowerCase()

      .replace(
        /[^\p{L}\p{N}\s₹+&-]/gu,
        ' '
      )

      .replace(
        /\s+/g,
        ' '
      )

      .trim();

  }


  function tokens(value, removeStops) {

    const result =
      normalize(value)
        .split(' ')
        .filter(Boolean);


    if (!removeStops) {
      return result;
    }


    return result.filter(function (token) {

      return (
        token.length > 1 &&
        !STOP_WORDS.has(token)
      );

    });

  }



  /* =========================================================
     TYPO MATCHING
     ========================================================= */

  function levenshtein(a, b) {

    a = String(a || '');
    b = String(b || '');


    if (a === b) {
      return 0;
    }


    if (!a.length) {
      return b.length;
    }


    if (!b.length) {
      return a.length;
    }


    const matrix = [];


    for (let i = 0; i <= b.length; i += 1) {
      matrix[i] = [i];
    }


    for (let j = 0; j <= a.length; j += 1) {
      matrix[0][j] = j;
    }


    for (let i = 1; i <= b.length; i += 1) {

      for (let j = 1; j <= a.length; j += 1) {

        if (
          b.charAt(i - 1) ===
          a.charAt(j - 1)
        ) {

          matrix[i][j] =
            matrix[i - 1][j - 1];

        } else {

          matrix[i][j] =
            Math.min(

              matrix[i - 1][j - 1] + 1,

              matrix[i][j - 1] + 1,

              matrix[i - 1][j] + 1

            );

        }

      }

    }


    return matrix[b.length][a.length];

  }


  function fuzzyTokenMatch(a, b) {

    a = normalize(a);
    b = normalize(b);


    if (!a || !b) {
      return false;
    }


    if (a === b) {
      return true;
    }


    if (
      a.length >= 4 &&
      b.length >= 4 &&
      (
        a.includes(b) ||
        b.includes(a)
      )
    ) {

      return true;

    }


    if (
      Math.min(
        a.length,
        b.length
      ) < 4
    ) {

      return false;

    }


    const distance =
      levenshtein(a, b);


    const maxLength =
      Math.max(
        a.length,
        b.length
      );


    if (
      maxLength >= 8 &&
      distance <= 2
    ) {

      return true;

    }


    return (
      distance <= 1
    );

  }


  function containsFuzzy(
    query,
    target
  ) {

    const qTokens =
      tokens(query, false);


    const targetTokens =
      tokens(target, false);


    if (!targetTokens.length) {
      return false;
    }


    return targetTokens.every(
      function (targetToken) {

        return qTokens.some(
          function (queryToken) {

            return fuzzyTokenMatch(
              queryToken,
              targetToken
            );

          }
        );

      }
    );

  }



  /* =========================================================
     CURATED VEDICA KNOWLEDGE
     ========================================================= */

  const TOPICS = [


    /* ---------------- ABOUT ---------------- */

    {
      id: 'about',

      priority: 100,

      patterns: [
        /\bwhat\s+is\s+vedica(\s+artistry)?\b/i,
        /\bwho\s+is\s+vedica(\s+artistry)?\b/i,
        /\babout\s+(vedica|vedica\s+artistry|your\s+brand)\b/i,
        /\btell\s+(me\s+)?about\s+(vedica|vedica\s+artistry|your\s+brand)\b/i
      ],

      keywords: [
        'vedica',
        'vedica artistry',
        'about vedica',
        'about vedica artistry',
        'about brand',
        'brand story',
        'what is vedica',
        'what is vedica artistry',
        'tell about vedica'
      ],

      answer:
        "Vedica Artistry is a homegrown Indian lifestyle brand founded in 2023 by Megha Chakraborty in Kolkata. ✨\n\n" +

        "We bring Indian tradition into modern everyday life through Indo-Western fashion, ethnic wear, contemporary apparel, handmade crafts, handicrafts, home décor, accessories and artistic lifestyle products.\n\n" +

        "Vedica is rooted in Indian craftsmanship, Bengali emotion, nostalgia and artisan-led creativity — but designed with a fresh contemporary character.",

      links: [
        {
          label:
            'Discover our story →',

          url:
            '/pages/about-us'
        },

        {
          label:
            'Shop Vedica →',

          url:
            '/collections/all'
        }
      ]
    },


    /* ---------------- FOUNDER ---------------- */

    {
      id: 'founder',

      priority: 95,

      patterns: [
        /\bwho\s+(founded|owns|started|created)\s+(vedica|vedica\s+artistry)\b/i,
        /\b(founder|owner|proprietor)\s+(of\s+)?vedica\b/i
      ],

      keywords: [
        'founder',
        'owner',
        'proprietor',
        'megha',
        'megha chakraborty',
        'who founded vedica',
        'who owns vedica',
        'vedica founder',
        'vedica owner'
      ],

      answer:
        "Vedica Artistry was founded in 2023 by Megha Chakraborty, a Mathematics teacher from Kolkata whose creative passion grew into a brand centred around Indian artistry, fashion, handcrafted beauty and modern living.\n\n" +

        "Megha Chakraborty is also the legal proprietor of Vedica Artistry.",

      links: [
        {
          label:
            'Meet the founder →',

          url:
            '/pages/about-us'
        }
      ]
    },


    /* ---------------- PHYSICAL STORE ---------------- */

    {
      id: 'store',

      priority: 98,

      patterns: [
        /\b(store|shop)\s+locator\b/i,
        /\b(physical|offline|retail)\s+(store|shop|outlet)\b/i,
        /\b(store|shop)\s+near\s+me\b/i,
        /\b(showroom|retail\s+outlet)\b/i,
        /\bdo\s+(you|u)\s+(have|own)\s+(a\s+)?(physical\s+)?(store|shop)\b/i,
        /\bwhere\s+(is|are)\s+(your\s+)?(store|shop|outlet)\b/i
      ],

      keywords: [
        'store',
        'store locator',
        'shop locator',
        'physical store',
        'physical shop',
        'offline store',
        'offline shop',
        'showroom',
        'outlet',
        'store near me',
        'shop near me'
      ],

      answer:
        "Vedica Artistry does not currently operate a physical retail store, showroom or store-locator network. 🛍️\n\n" +

        "You can shop Vedica online through our official website. Our registered/principal business address is a legal and business correspondence address — it should not be treated as a walk-in retail outlet unless Vedica specifically announces otherwise.",

      links: [
        {
          label:
            'Shop online →',

          url:
            '/collections/all'
        },

        {
          label:
            'Contact Vedica →',

          url:
            '/pages/contact-us'
        }
      ]
    },


    /* ---------------- LOCATION ---------------- */

    {
      id: 'location',

      priority: 85,

      patterns: [
        /\bwhere\s+(is|are)\s+(vedica|you)\s+based\b/i,
        /\bregistered\s+(office|address)\b/i,
        /\bbusiness\s+address\b/i
      ],

      keywords: [
        'where are you based',
        'where is vedica based',
        'location',
        'kolkata',
        'west bengal',
        'banshdroni',
        'business address',
        'registered address',
        'principal address'
      ],

      answer:
        "Vedica Artistry is a Kolkata-based Indian brand. Its registered/principal business address is Floor No. 2, Building No./Flat No. 75, Roynagar, Madhya Para, Banshdroni, Kolkata, West Bengal – 700070, India.\n\n" +

        "This is the registered/principal business address, not a walk-in retail showroom.",

      links: [
        {
          label:
            'Legal information →',

          url:
            '/pages/legal-notice'
        }
      ]
    },


    /* ---------------- PRODUCTS ---------------- */

    {
      id: 'products',

      priority: 75,

      patterns: [
        /\bwhat\s+(do|does)\s+(you|vedica)\s+sell\b/i,
        /\bwhat\s+can\s+i\s+buy\b/i,
        /\bshow\s+(me\s+)?(your\s+)?products\b/i
      ],

      keywords: [
        'what do you sell',
        'products',
        'categories',
        'fashion',
        'clothing',
        'women clothing',
        'dress',
        'dresses',
        'top wear',
        'western wear',
        'indo western',
        'ethnic wear',
        'home decor',
        'terracotta',
        'handicraft',
        'handicrafts',
        'accessories'
      ],

      answer:
        "Vedica Artistry brings fashion, craft and artistic living together. Our brand universe includes Indo-Western fashion, ethnic wear, contemporary apparel, handmade crafts, handicrafts, accessories and home décor.\n\n" +

        "You can currently explore clothing, dresses, top wear and handcrafted Home Décor through the online store.",

      links: [
        {
          label:
            'Shop all →',

          url:
            '/collections/all'
        },

        {
          label:
            'Women Clothing →',

          url:
            '/collections/women'
        },

        {
          label:
            'Home Décor →',

          url:
            '/collections/home-decor'
        }
      ]
    },


    /* ---------------- SHIPPING ---------------- */

    {
      id: 'shipping',

      priority: 90,

      patterns: [
        /\bshipping\s+policy\b/i,
        /\bdelivery\s+policy\b/i
      ],

      keywords: [
        'shipping policy',
        'delivery policy',
        'shipping rules',
        'dispatch time',
        'delivery time'
      ],

      answer:
        "Standard Vedica Artistry orders are generally processed within 3–4 business days. Bulk, made-to-order or customized orders may require approximately 7–15 business days unless another timeline is stated.\n\n" +

        "After dispatch, normal estimated domestic transit is approximately 3–5 business days for metros, 4–7 for non-metros and 6–10 for remote or extended-service areas. These are estimates rather than guaranteed delivery dates.",

      links: [
        {
          label:
            'Read Shipping Policy →',

          url:
            '/pages/shipping-policy'
        }
      ]
    },


    /* ---------------- RETURNS ---------------- */

    {
      id: 'returns',

      priority: 91,

      patterns: [
        /\b(return|exchange)\s+policy\b/i,
        /\bcan\s+i\s+(return|exchange)\b/i
      ],

      keywords: [
        'return policy',
        'exchange policy',
        'returns',
        'exchange',
        'wrong size',
        'change mind'
      ],

      answer:
        "Vedica Artistry does not generally provide a change-of-mind return merely because a customer changes their preference, unless another right is stated for that product or required by applicable law.\n\n" +

        "Damaged, defective, wrong, missing or otherwise eligible cases are reviewed according to the Exchange & Return Policy.",

      links: [
        {
          label:
            'Exchange & Return Policy →',

          url:
            '/pages/exchange-return-policy'
        }
      ]
    },


    /* ---------------- REFUNDS ---------------- */

    {
      id: 'refund',

      priority: 92,

      patterns: [
        /\brefund\s+policy\b/i,
        /\bhow\s+long\s+(does|will)\s+(a\s+)?refund\b/i
      ],

      keywords: [
        'refund',
        'refund policy',
        'refund rules',
        'refund time',
        'refund timeline',
        'money back',
        'cod refund'
      ],

      answer:
        "After an eligible refund has been approved and successfully initiated by Vedica Artistry, it generally takes approximately 7–10 banking days to reflect. The actual posting time can depend on the bank, card issuer, payment gateway or payment provider.",

      links: [
        {
          label:
            'Read Refund Policy →',

          url:
            '/pages/refund-policy'
        }
      ]
    },


    /* ---------------- CANCELLATION ---------------- */

    {
      id: 'cancellation',

      priority: 90,

      patterns: [
        /\bcancellation\s+policy\b/i,
        /\bhow\s+(do|can)\s+i\s+cancel\b/i
      ],

      keywords: [
        'cancellation policy',
        'cancel policy',
        'cancel',
        'cancellation'
      ],

      answer:
        "Cancellation requests should be submitted as early as possible, generally within 24 hours of placing the order. Eligibility still depends on the order's fulfilment status, so a request within 24 hours isn't an automatic guarantee that cancellation remains possible.",

      links: [
        {
          label:
            'Cancellation Policy →',

          url:
            '/pages/cancellation-policy'
        }
      ]
    },


    /* ---------------- PRIVACY ---------------- */

    {
      id: 'privacy',

      priority: 90,

      patterns: [
        /\bprivacy\s+policy\b/i,
        /\bhow\s+do\s+you\s+(use|collect|protect)\s+(my\s+)?data\b/i
      ],

      keywords: [
        'privacy',
        'privacy policy',
        'data policy',
        'personal data',
        'personal information'
      ],

      answer:
        "Vedica Artistry's Privacy Policy explains how information may be collected, used, retained, shared and protected when customers visit the store, contact Vedica, create an account or make a purchase.",

      links: [
        {
          label:
            'Read Privacy Policy →',

          url:
            '/pages/privacy-policy'
        }
      ]
    },


    /* ---------------- TERMS ---------------- */

    {
      id: 'terms',

      priority: 80,

      patterns: [
        /\bterms\s+(of\s+service|and\s+conditions)\b/i
      ],

      keywords: [
        'terms',
        'terms of service',
        'terms and conditions'
      ],

      answer:
        "Vedica Artistry's Terms of Service govern use of the online store and related services, including accounts, products, orders, pricing, transactions and other store conditions.",

      links: [
        {
          label:
            'Terms of Service →',

          url:
            '/pages/terms-of-service'
        }
      ]
    },


    /* ---------------- LEGAL ---------------- */

    {
      id: 'legal',

      priority: 89,

      patterns: [
        /\b(gst|gstin|udyam)\b/i,
        /\blegal\s+notice\b/i
      ],

      keywords: [
        'legal',
        'legal notice',
        'gst',
        'gstin',
        'udyam',
        'legal name',
        'business details',
        'registration'
      ],

      answer:
        "Vedica Artistry is a proprietorship owned and operated by Megha Chakraborty.\n\n" +

        "Trade Name: Vedica Artistry\n" +
        "Legal Name / Proprietor: Megha Chakraborty\n" +
        "GSTIN: 19BXRPC8957R1Z4\n" +
        "Udyam Registration No.: UDYAM-WB-18-0196163",

      links: [
        {
          label:
            'Legal Notice →',

          url:
            '/pages/legal-notice'
        }
      ]
    },


    /* ---------------- CONTACT ---------------- */

    {
      id: 'contact',

      priority: 88,

      patterns: [
        /\bhow\s+(do|can)\s+i\s+contact\b/i,
        /\bcustomer\s+(care|support)\b/i
      ],

      keywords: [
        'contact',
        'contact us',
        'customer care',
        'customer support',
        'whatsapp',
        'phone',
        'email',
        'support hours',
        'working hours'
      ],

      answer:
        "Vedica Artistry customer care is available Monday–Saturday, 11:00 AM–7:00 PM IST.\n\n" +

        "Email: care@vedicaonline.com\n" +
        "Phone / WhatsApp: +91 7003301386",

      links: [
        {
          label:
            'Contact Us →',

          url:
            '/pages/contact-us'
        },

        {
          label:
            'WhatsApp Vedica →',

          url:
            'https://wa.me/917003301386',

          external:
            true
        }
      ]
    },


    /* ---------------- INTERNATIONAL ---------------- */

    {
      id: 'international',

      priority: 78,

      patterns: [
        /\bdo\s+you\s+ship\s+(internationally|abroad|outside\s+india)\b/i,
        /\binternational\s+shipping\b/i
      ],

      keywords: [
        'international',
        'international shipping',
        'ship abroad',
        'outside india',
        'overseas',
        'customs'
      ],

      answer:
        "International shipping may be available to selected serviceable destinations. Delivery timelines depend on destination, courier service and customs clearance. Destination-country duties, taxes or similar charges may also apply where they aren't included at checkout.",

      links: [
        {
          label:
            'Shipping Policy →',

          url:
            '/pages/shipping-policy'
        }
      ]
    },


    /* ---------------- BUSINESS ENQUIRIES ---------------- */

    {
      id: 'business',

      priority: 76,

      patterns: [
        /\b(collab|collaboration|wholesale|artisan\s+partnership|media\s+enquir)/i,
        /\bwork\s+with\s+(vedica|you)\b/i
      ],

      keywords: [
        'collaboration',
        'collab',
        'wholesale',
        'partnership',
        'artisan partnership',
        'media enquiry',
        'business enquiry',
        'work with vedica'
      ],

      answer:
        "For collaborations, wholesale enquiries, artisan partnerships, media requests or other business enquiries, contact Vedica Artistry at care@vedicaonline.com and clearly mention the nature of the enquiry.",

      links: [
        {
          label:
            'Contact Vedica →',

          url:
            '/pages/contact-us'
        }
      ]
    },


    /* ---------------- ALL POLICIES ---------------- */

    {
      id: 'policies',

      priority: 50,

      patterns: [
        /\b(show|give|list|open)\s+(me\s+)?(all\s+)?(your\s+)?polic(y|ies)\b/i,
        /\ball\s+polic(y|ies)\b/i
      ],

      keywords: [
        'policy',
        'policies',
        'policy page',
        'policy pages',
        'policy links',
        'all policies',
        'legal pages'
      ],

      answer:
        "Here are Vedica Artistry's main customer-care and legal pages. Choose the one you need:",

      links: [

        {
          label:
            'Shipping Policy →',
          url:
            '/pages/shipping-policy'
        },

        {
          label:
            'Exchange & Return →',
          url:
            '/pages/exchange-return-policy'
        },

        {
          label:
            'Refund Policy →',
          url:
            '/pages/refund-policy'
        },

        {
          label:
            'Cancellation Policy →',
          url:
            '/pages/cancellation-policy'
        },

        {
          label:
            'Privacy Policy →',
          url:
            '/pages/privacy-policy'
        },

        {
          label:
            'Terms of Service →',
          url:
            '/pages/terms-of-service'
        },

        {
          label:
            'Legal Notice →',
          url:
            '/pages/legal-notice'
        },

        {
          label:
            'Contact Us →',
          url:
            '/pages/contact-us'
        }

      ]
    }

  ];



  /* =========================================================
     TOPIC SCORING
     ========================================================= */

  function topicScore(
    message,
    topic
  ) {

    const raw =
      String(message || '');


    const query =
      normalize(raw);


    let score =
      0;


    topic.patterns.forEach(
      function (pattern) {

        pattern.lastIndex =
          0;


        if (
          pattern.test(raw)
        ) {

          score +=
            200;

        }

      }
    );


    topic.keywords.forEach(
      function (keyword) {

        const clean =
          normalize(keyword);


        if (
          query ===
          clean
        ) {

          score +=
            90;

        }


        else if (
          query.includes(
            clean
          )
        ) {

          score +=
            45;

        }


        else if (
          containsFuzzy(
            query,
            clean
          )
        ) {

          score +=
            20;

        }

      }
    );


    return (
      score +
      topic.priority
    );

  }


  function matchTopic(
    message
  ) {

    const ranked =
      TOPICS

        .map(
          function (topic) {

            return {

              topic:
                topic,

              score:
                topicScore(
                  message,
                  topic
                )

            };

          }
        )

        .sort(
          function (a, b) {

            return (
              b.score -
              a.score
            );

          }
        );


    if (
      !ranked.length ||
      ranked[0].score <
      70
    ) {

      return null;

    }


    return ranked[0].topic;

  }



  /* =========================================================
     PRIVATE DETECTION
     ========================================================= */

  function isPrivateQuestion(
    message
  ) {

    return PRIVATE_PATTERNS.some(
      function (pattern) {

        pattern.lastIndex =
          0;

        return pattern.test(
          String(message || '')
        );

      }
    );

  }



  /* =========================================================
     INIT
     ========================================================= */

  function initAgent(root) {

    if (
      !root ||
      root.dataset.vaAgentReady ===
        'true'
    ) {

      return;

    }


    root.dataset.vaAgentReady =
      'true';


    const faqRoot =

      root.closest(
        '[data-va-faq-root]'
      ) ||

      document.querySelector(
        '[data-va-faq-root]'
      );


    const form =
      root.querySelector(
        '[data-va-agent-form]'
      );


    const input =
      root.querySelector(
        '[data-va-agent-input]'
      );


    const messages =
      root.querySelector(
        '[data-va-agent-messages]'
      );


    const thinking =
      root.querySelector(
        '[data-va-agent-thinking]'
      );


    const status =
      root.querySelector(
        '[data-va-agent-status]'
      );


    const quickButtons =
      Array.from(
        root.querySelectorAll(
          '[data-va-agent-prompt]'
        )
      );


    let busy =
      false;



    /* ======================================================
       READ FAQ ACCORDIONS
       ====================================================== */

    const faqItems =

      faqRoot

        ? Array.from(
            faqRoot.querySelectorAll(
              '[data-va-faq-item]'
            )
          )

        : [];


    const faqKnowledge =
      faqItems.map(
        function (item) {


          const questionNode =
            item.querySelector(
              '[data-va-faq-question] strong'
            );


          const answerNode =
            item.querySelector(
              '.va-faq__answer-content'
            );


          const question =
            questionNode

              ? questionNode
                  .textContent
                  .trim()

              : '';


          const answer =
            answerNode

              ? answerNode
                  .textContent
                  .replace(
                    /\s+/g,
                    ' '
                  )
                  .trim()

              : '';


          return {

            id:
              item.id,

            question:
              question,

            answer:
              answer,

            category:
              item.dataset.category || '',

            searchTokens:
              tokens(
                question +
                ' ' +
                answer +
                ' ' +
                (
                  item.dataset.category ||
                  ''
                ),
                true
              )

          };

        }
      );



    /* ======================================================
       UI HELPERS
       ====================================================== */

    function setStatus(text) {

      if (status) {

        status.textContent =
          text || '';

      }

    }


    function scrollChat() {

      if (messages) {

        messages.scrollTop =
          messages.scrollHeight;

      }

    }


    function showThinking(show) {

      if (!thinking) {
        return;
      }


      thinking.hidden =
        !show;


      if (show) {
        scrollChat();
      }

    }



    /* ======================================================
       ADD CHAT MESSAGE
       ====================================================== */

    function addMessage(
      role,
      text,
      options
    ) {

      if (!messages) {
        return;
      }


      const opts =
        options || {};


      const row =
        document.createElement(
          'div'
        );


      row.className =

        'va-agent__message ' +

        (
          role === 'user'

            ? 'va-agent__message--user'

            : 'va-agent__message--assistant'
        );


      const bubble =
        document.createElement(
          'div'
        );


      bubble.className =
        'va-agent__bubble';


      const label =
        document.createElement(
          'span'
        );


      label.className =
        'va-agent__message-label';


      label.textContent =
        role === 'user'
          ? 'You'
          : 'Veda';


      const body =
        document.createElement(
          'div'
        );


      body.textContent =
        text;


      bubble.appendChild(label);
      bubble.appendChild(body);



      if (opts.targetId) {

        const faqLink =
          document.createElement(
            'a'
          );


        faqLink.className =
          'va-agent__answer-link';


        faqLink.href =
          '#' +
          opts.targetId;


        faqLink.textContent =
          'View detailed FAQ ↓';


        faqLink.addEventListener(
          'click',
          function (event) {


            event.preventDefault();


            const target =
              document.getElementById(
                opts.targetId
              );


            if (!target) {
              return;
            }


            target.hidden =
              false;


            target.classList.add(
              'is-open'
            );


            const question =
              target.querySelector(
                '[data-va-faq-question]'
              );


            const answer =
              target.querySelector(
                '[data-va-faq-answer]'
              );


            if (question) {

              question.setAttribute(
                'aria-expanded',
                'true'
              );

            }


            if (answer) {

              answer.setAttribute(
                'aria-hidden',
                'false'
              );

            }


            target.scrollIntoView({

              behavior:
                'smooth',

              block:
                'center'

            });

          }
        );


        bubble.appendChild(
          faqLink
        );

      }



      if (
        Array.isArray(opts.links)
      ) {

        opts.links.forEach(
          function (linkData) {


            const link =
              document.createElement(
                'a'
              );


            link.className =
              'va-agent__answer-link';


            link.href =
              linkData.url;


            link.textContent =
              linkData.label;


            if (
              linkData.external
            ) {

              link.target =
                '_blank';


              link.rel =
                'noopener noreferrer';

            }


            bubble.appendChild(
              link
            );

          }
        );

      }


      row.appendChild(
        bubble
      );


      messages.appendChild(
        row
      );


      scrollChat();

    }



    /* ======================================================
       GENERIC FAQ MATCHING
       ====================================================== */

    function faqScore(
      message,
      entry
    ) {

      const queryTokens =
        tokens(
          message,
          true
        );


      if (!queryTokens.length) {
        return 0;
      }


      let score =
        0;


      queryTokens.forEach(
        function (queryToken) {


          const matched =
            entry.searchTokens.some(
              function (faqToken) {

                return fuzzyTokenMatch(
                  queryToken,
                  faqToken
                );

              }
            );


          if (matched) {

            score +=

              queryToken.length >= 6
                ? 5
                : 3;

          }

        }
      );


      const questionTokens =
        tokens(
          entry.question,
          true
        );


      queryTokens.forEach(
        function (queryToken) {

          if (
            questionTokens.some(
              function (questionToken) {

                return fuzzyTokenMatch(
                  queryToken,
                  questionToken
                );

              }
            )
          ) {

            score +=
              4;

          }

        }
      );


      return score;

    }


    function findFaqMatches(
      message
    ) {

      return faqKnowledge

        .map(
          function (entry) {

            return {

              entry:
                entry,

              score:
                faqScore(
                  message,
                  entry
                )

            };

          }
        )

        .filter(
          function (result) {

            return result.score > 0;

          }
        )

        .sort(
          function (a, b) {

            return (
              b.score -
              a.score
            );

          }
        );

    }



    /* ======================================================
       PROCESS QUESTION
       ====================================================== */

    function processQuestion(
      rawMessage
    ) {

      const message =
        String(rawMessage || '')
          .trim();


      if (
        !message ||
        busy
      ) {

        return;

      }


      busy =
        true;


      setStatus('');


      if (input) {
        input.value = '';
      }


      addMessage(
        'user',
        message
      );


      showThinking(true);


      window.setTimeout(
        function () {


          showThinking(false);



          /* ------------------------------------------
             PRIVATE FIRST
             ------------------------------------------ */

          if (
            isPrivateQuestion(
              message
            )
          ) {

            addMessage(

              'assistant',

              "That request involves private customer or order information. 🔐\n\n" +

              "Veda's secure customer-verification system isn't active yet, so I can't access order-specific tracking, payment, customer-account or refund updates.\n\n" +

              "Please contact customer care for private order assistance.",

              {
                links: [
                  {
                    label:
                      'Contact customer care →',

                    url:
                      '/pages/contact-us'
                  }
                ]
              }

            );


            busy = false;
            return;

          }



          /* ------------------------------------------
             SMALL TALK
             ------------------------------------------ */

          if (
            GREETINGS.test(
              message
            )
          ) {

            addMessage(

              'assistant',

              "Hi! 👋 I'm Veda, Vedica Artistry's help assistant. Ask me about Vedica, our founder, products, physical-store availability, policies, delivery, returns, refunds, contact details or other general shopping questions."

            );


            busy = false;
            return;

          }


          if (
            THANKS.test(
              message
            )
          ) {

            addMessage(

              'assistant',

              "You're very welcome! 💛 If there's anything else you'd like to know about Vedica Artistry, just ask."

            );


            busy = false;
            return;

          }


          if (
            BYE.test(
              message
            )
          ) {

            addMessage(

              'assistant',

              "Bye for now! ✨ Thank you for visiting Vedica Artistry."

            );


            busy = false;
            return;

          }


          if (
            AGENT_IDENTITY.test(
              message
            )
          ) {

            addMessage(

              'assistant',

              "I'm Veda — Vedica Artistry's digital help assistant. For now I answer public FAQs, brand information, policies and general shopping questions."

            );


            busy = false;
            return;

          }



          /* ------------------------------------------
             CURATED VEDICA KNOWLEDGE BEFORE FAQ
             ------------------------------------------ */

          const topic =
            matchTopic(
              message
            );


          if (topic) {

            addMessage(

              'assistant',

              topic.answer,

              {
                links:
                  topic.links || []
              }

            );


            setStatus(
              'Vedica information found.'
            );


            busy = false;
            return;

          }



          /* ------------------------------------------
             FAQ DATABASE
             ------------------------------------------ */

          const faqMatches =
            findFaqMatches(
              message
            );


          if (
            faqMatches.length &&
            faqMatches[0].score >= 8
          ) {

            const best =
              faqMatches[0].entry;


            addMessage(

              'assistant',

              best.answer,

              {
                targetId:
                  best.id
              }

            );


            setStatus(
              'Best matching FAQ found.'
            );


            busy = false;
            return;

          }



          /* ------------------------------------------
             RELATED FAQ SUGGESTIONS
             ------------------------------------------ */

          if (
            faqMatches.length &&
            faqMatches[0].score >= 4
          ) {

            const suggestions =
              faqMatches
                .slice(0, 3)
                .map(
                  function (
                    match,
                    index
                  ) {

                    return (

                      (index + 1) +
                      '. ' +
                      match.entry.question

                    );

                  }
                )
                .join('\n');


            addMessage(

              'assistant',

              "I found a few related FAQs:\n\n" +

              suggestions +

              "\n\nAdd one or two more words and I'll narrow it down."

            );


            busy = false;
            return;

          }



          /* ------------------------------------------
             NO ANSWER
             ------------------------------------------ */

          addMessage(

            'assistant',

            "I don't have a confident answer for that one yet. 🤔\n\n" +

            "Try asking about Vedica Artistry, Megha Chakraborty, our physical store, products, home décor, shipping, returns, refunds, cancellations, policies, legal details, international shipping, collaborations or customer care."

          );


          busy = false;


        },
        220
      );

    }



    /* ======================================================
       SUBMIT / ENTER
       ====================================================== */

    if (form) {

      form.addEventListener(
        'submit',
        function (event) {

          event.preventDefault();


          processQuestion(

            input
              ? input.value
              : ''

          );

        }
      );

    }


    quickButtons.forEach(
      function (button) {

        button.addEventListener(
          'click',
          function () {

            processQuestion(

              button.dataset
                .vaAgentPrompt ||
              ''

            );

          }
        );

      }
    );



    /* ======================================================
       WELCOME
       ====================================================== */

    addMessage(

      'assistant',

      "Hi! 👋 I'm Veda. Ask me about Vedica Artistry, our founder, products, policies, store availability, delivery, returns or customer care. Short keywords and imperfect spelling are okay too."

    );


    setStatus(

      TOPICS.length +
      ' Vedica topics + ' +
      faqKnowledge.length +
      ' FAQ answers ready.'

    );

  }



  /* =========================================================
     BOOT
     ========================================================= */

  function initAll(scope) {

    const context =
      scope || document;


    if (
      context.matches &&
      context.matches(
        '[data-va-agent]'
      )
    ) {

      initAgent(context);

    }


    context
      .querySelectorAll(
        '[data-va-agent]'
      )
      .forEach(
        initAgent
      );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      function () {

        initAll(document);

      }
    );

  } else {

    initAll(document);

  }


  document.addEventListener(
    'shopify:section:load',
    function (event) {

      initAll(
        event.target
      );

    }
  );

})();